import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import contentService from '../services/contentService';
import PostDetailModal from '../components/PostDetailModal';
import PublicarManualModal from '../components/PublicarManualModal';
import InformeModal from '../components/InformeModal';
import Loading from '../components/Loading';
import authService from '../services/authService';
import './ProgramacionPost.css';

const ProgramacionPost = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('proceso'); // 'proceso', 'aprobados', 'programados', 'publicados'
  const [filtros, setFiltros] = useState({
    plataforma: '',
    desarrolla: '',
    busqueda: ''
  });

  // Estados para filtros de influencer
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);
  const [availableInfluencers, setAvailableInfluencers] = useState([]);

  // Estado para modal de informe
  const [showInformeModal, setShowInformeModal] = useState(false);

  // Obtener usuario actual
  const user = authService.getUser();
  const isAprobadorMaestro = user && user.groups && user.groups.includes('Aprobador Maestro');

  // Cargar posts cuando cambia el filtro de estado
  useEffect(() => {
    loadPosts();
  }, [filtroEstado]);

  // Mapeo de filtro de estado a estados del backend
  const getStatusFilter = (filtro) => {
    switch (filtro) {
      case 'aprobados':
        return 'approved';
      case 'programados':
        return 'scheduled';
      case 'publicados':
        return 'published';
      case 'proceso':
      default:
        return 'in_progress'; // Estados en proceso
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      // Construir parámetros de filtro para el backend
      const params = {};

      // Para "en proceso" excluimos los estados finales
      // Para los demás, filtramos directamente por el estado
      if (filtroEstado === 'proceso') {
        // Excluir estados: approved, scheduled, published
        params.exclude_status = 'approved,scheduled,published';
      } else {
        params.status = getStatusFilter(filtroEstado);
      }

      const response = await contentService.getPosts(params);

      // Manejar diferentes estructuras de respuesta
      let allPosts = [];
      if (Array.isArray(response.data)) {
        allPosts = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        allPosts = response.data.results;
      } else if (response.data.posts && Array.isArray(response.data.posts)) {
        allPosts = response.data.posts;
      } else {
        console.error('Estructura de respuesta inesperada:', response.data);
        allPosts = [];
      }

      console.log(`✅ Posts cargados (${filtroEstado}):`, allPosts.length);
      setPosts(allPosts);

      // Extraer influencers únicos
      const influencersMap = new Map();
      allPosts.forEach(post => {
        if (post.influencer && post.influencer_name) {
          if (!influencersMap.has(post.influencer)) {
            influencersMap.set(post.influencer, {
              id: post.influencer,
              name: post.influencer_name,
              profileImage: post.influencer_profile_image_url
            });
          }
        }
      });
      setAvailableInfluencers(Array.from(influencersMap.values()));
    } catch (error) {
      console.error('Error loading posts:', error);
      alert('Error al cargar posts');
    } finally {
      setLoading(false);
    }
  };

  // Predicciones de engagement deshabilitadas para mejorar rendimiento
  // Se pueden cargar bajo demanda en el futuro si es necesario

  const handleOpenDetail = (post) => {
    setSelectedPost(post);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedPost(null);
    loadPosts(); // Recargar por si hubo cambios
  };

  const handleInfluencerFilterClick = (influencerId) => {
    setSelectedInfluencers(prev => {
      if (prev.includes(influencerId)) {
        return prev.filter(id => id !== influencerId);
      } else {
        return [...prev, influencerId];
      }
    });
  };

  const clearAllFilters = () => {
    setFiltros({ plataforma: '', desarrolla: '', busqueda: '' });
    setSelectedInfluencers([]);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Filtrar posts con filtros adicionales (plataforma, desarrolla, búsqueda, influencer)
  // Nota: El filtro por estado ya viene del servidor
  const postsFiltrados = posts.filter(post => {
    if (filtros.plataforma && post.platform !== parseInt(filtros.plataforma)) {
      return false;
    }
    if (filtros.desarrolla && post.desarrolla !== parseInt(filtros.desarrolla)) {
      return false;
    }
    if (selectedInfluencers.length > 0 && !selectedInfluencers.includes(post.influencer)) {
      return false;
    }
    if (filtros.busqueda) {
      const searchLower = filtros.busqueda.toLowerCase();
      const matchesSearch =
        post.title?.toLowerCase().includes(searchLower) ||
        post.platform_name?.toLowerCase().includes(searchLower) ||
        post.influencer_name?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }
    return true;
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="programacion-post-container">
      {/* Header */}
      <div className="programacion-header">
        <div>
          <h1 className="programacion-title">
            {filtroEstado === 'proceso' && 'Posts En Proceso'}
            {filtroEstado === 'aprobados' && 'Posts Aprobados'}
            {filtroEstado === 'programados' && 'Posts Programados'}
            {filtroEstado === 'publicados' && 'Posts Publicados'}
          </h1>
          <p className="programacion-subtitle">
            {filtroEstado === 'proceso' && 'Contenido en desarrollo'}
            {filtroEstado === 'aprobados' && 'Contenido listo para publicar'}
            {filtroEstado === 'programados' && 'Contenido programado para publicación'}
            {filtroEstado === 'publicados' && 'Contenido ya publicado'}
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowInformeModal(true)}
            className="view-switch-btn"
            title="Generar Informe"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem'
            }}
          >
            📊 Informe
          </button>
          <button
            onClick={() => navigate('/calendario')}
            className="view-switch-btn"
            title="Ver Calendario"
          >
            📅
          </button>
        </div>
      </div>

      {/* Botones de filtro por estado y búsqueda */}
      <div className="estado-filter-buttons">
        <button
          className={`estado-btn ${filtroEstado === 'proceso' ? 'active' : ''}`}
          onClick={() => setFiltroEstado('proceso')}
        >
          🔄 En Proceso
        </button>
        <button
          className={`estado-btn ${filtroEstado === 'aprobados' ? 'active' : ''}`}
          onClick={() => setFiltroEstado('aprobados')}
        >
          ✅ Aprobados
        </button>
        <button
          className={`estado-btn ${filtroEstado === 'programados' ? 'active' : ''}`}
          onClick={() => setFiltroEstado('programados')}
        >
          📅 Programados
        </button>
        <button
          className={`estado-btn ${filtroEstado === 'publicados' ? 'active' : ''}`}
          onClick={() => setFiltroEstado('publicados')}
        >
          🚀 Publicados
        </button>

        {/* Botón de Mostrar/Ocultar Filtros */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`estado-btn ${showFilters ? 'active' : ''}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            position: 'relative'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>{showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}</span>
          {selectedInfluencers.length > 0 && (
            <span style={{
              backgroundColor: showFilters ? 'white' : '#69B32D',
              color: showFilters ? '#69B32D' : 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              position: 'absolute',
              top: '-5px',
              right: '-5px'
            }}>
              {selectedInfluencers.length}
            </span>
          )}
        </button>

        <input
          type="text"
          placeholder="🔍 Buscar..."
          value={filtros.busqueda}
          onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
          className="calendar-search-input"
        />
      </div>

      {/* Sección de filtros de Influencer */}
      {showFilters && availableInfluencers.length > 0 && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {availableInfluencers.map(influencer => {
              const isSelected = selectedInfluencers.includes(influencer.id);

              return (
                <button
                  key={influencer.id}
                  onClick={() => handleInfluencerFilterClick(influencer.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    border: isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: isSelected ? '#3b82f6' : 'white',
                    color: isSelected ? '#ffffff' : '#374151',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? '600' : '500',
                    transition: 'all 0.2s',
                    opacity: isSelected ? 1 : 0.7
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.opacity = '1';
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.opacity = '0.7';
                  }}
                >
                  {influencer.profileImage ? (
                    <img
                      src={influencer.profileImage}
                      alt={influencer.name}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: isSelected ? '#ffffff' : '#3b82f6',
                      color: isSelected ? '#3b82f6' : '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {getInitials(influencer.name)}
                    </div>
                  )}
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? '600' : '500',
                    color: isSelected ? '#ffffff' : '#1f2937'
                  }}>
                    {influencer.name}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{
            marginTop: '0.75rem',
            fontSize: '0.75rem',
            color: '#6b7280',
            display: 'flex',
            gap: '1rem'
          }}>
            <span>Total: {postsFiltrados.length} posts</span>
            {selectedInfluencers.length > 0 && (
              <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                Filtrados por influencer
              </span>
            )}
          </div>
        </div>
      )}

      {/* Botón limpiar filtros */}
      {(filtros.plataforma || filtros.desarrolla || filtros.busqueda || selectedInfluencers.length > 0) && (
        <div className="programacion-filters">
          <button
            onClick={clearAllFilters}
            className="btn-clear-filters"
          >
            ✕ Limpiar filtros
          </button>
        </div>
      )}

      {/* Tabla de posts */}
      {postsFiltrados.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>
            {filtroEstado === 'proceso' && 'No hay posts en proceso'}
            {filtroEstado === 'aprobados' && 'No hay posts aprobados'}
            {filtroEstado === 'programados' && 'No hay posts programados'}
            {filtroEstado === 'publicados' && 'No hay posts publicados'}
          </h3>
          <p>
            {filtroEstado === 'proceso' && 'Los posts en proceso aparecerán aquí'}
            {filtroEstado === 'aprobados' && 'Los posts aprobados aparecerán aquí listos para publicar'}
            {filtroEstado === 'programados' && 'Los posts programados aparecerán aquí'}
            {filtroEstado === 'publicados' && 'Los posts publicados aparecerán aquí'}
          </p>
        </div>
      ) : (
        <PostsTable
          posts={postsFiltrados}
          onViewDetail={handleOpenDetail}
          navigate={navigate}
          filtroEstado={filtroEstado}
          isAprobadorMaestro={isAprobadorMaestro}
        />
      )}

      {/* Modal de detalle */}
      {showDetailModal && selectedPost && (
        <PostDetailModal
          postId={selectedPost.id}
          onClose={handleCloseDetail}
          onUpdate={loadPosts}
        />
      )}

      {/* Modal de informe */}
      <InformeModal
        isOpen={showInformeModal}
        onClose={() => setShowInformeModal(false)}
        availableInfluencers={availableInfluencers}
      />
    </div>
  );
};

// Componente de Tabla para mostrar posts
const PostsTable = ({ posts, onViewDetail, navigate, filtroEstado, isAprobadorMaestro }) => {
  const [publishing, setPublishing] = useState({});
  const [scheduling, setScheduling] = useState({});
  const [publishingStatus, setPublishingStatus] = useState({});
  const [approving, setApproving] = useState({});
  const [showPublicarManualModal, setShowPublicarManualModal] = useState(false);
  const [selectedPostForManual, setSelectedPostForManual] = useState(null);

  const handlePublish = async (post) => {
    if (!window.confirm('¿Estás seguro de cambiar el estado del post a "Publicado"?')) {
      return;
    }

    setPublishingStatus(prev => ({ ...prev, [post.id]: true }));
    try {
      const response = await contentService.updatePost(post.id, { status: 'published' });
      console.log('✅ Respuesta de cambio de estado:', response.data);
      alert('✅ Estado del post actualizado a publicado!');
      window.location.reload();
    } catch (error) {
      console.error('Error al cambiar estado del post:', error);
      const errorMsg = error.response?.data?.error || 'Error al cambiar el estado del post. Por favor, intenta de nuevo.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setPublishingStatus(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handlePublishNow = async (post) => {
    const platformName = post.platform?.name || 'la plataforma';
    if (!window.confirm(`¿Estás seguro de publicar este post inmediatamente en ${platformName}?`)) {
      return;
    }

    setPublishing(prev => ({ ...prev, [post.id]: true }));
    try {
      const response = await contentService.publishNow(post.id);
      console.log('✅ Respuesta de publicación:', response.data);
      alert(response.data.message || `✅ Post publicado exitosamente en ${platformName}!`);
      window.location.reload();
    } catch (error) {
      console.error('Error publicando post:', error);
      const errorMsg = error.response?.data?.error || 'Error al publicar el post. Por favor, intenta de nuevo.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setPublishing(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleSchedulePost = async (post) => {
    if (!window.confirm(`¿Programar este post para ${new Date(post.publication_date).toLocaleString('es-ES')}?`)) {
      return;
    }

    setScheduling(prev => ({ ...prev, [post.id]: true }));
    try {
      await contentService.schedulePost(post.id, post.publication_date);
      alert('✅ Post programado exitosamente!');
      window.location.reload();
    } catch (error) {
      console.error('Error programando post:', error);
      const errorMsg = error.response?.data?.error || 'Error al programar el post. Por favor, intenta de nuevo.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setScheduling(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleApprove = async (post) => {
    if (!window.confirm('¿Estás seguro de aprobar este post?')) {
      return;
    }

    setApproving(prev => ({ ...prev, [post.id]: true }));
    try {
      const response = await contentService.updatePost(post.id, { status: 'approved' });
      console.log('✅ Respuesta de aprobación:', response.data);
      alert('✅ Post aprobado exitosamente!');
      window.location.reload();
    } catch (error) {
      console.error('Error al aprobar el post:', error);
      const errorMsg = error.response?.data?.error || 'Error al aprobar el post. Por favor, intenta de nuevo.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setApproving(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleCancel = async (post) => {
    if (!window.confirm('¿Estás seguro de cancelar la programación de este post?')) {
      return;
    }

    setScheduling(prev => ({ ...prev, [post.id]: true }));
    try {
      const response = await contentService.updatePost(post.id, { status: 'approved' });
      console.log('✅ Respuesta de cancelación:', response.data);
      alert('✅ Programación cancelada exitosamente!');
      window.location.reload();
    } catch (error) {
      console.error('Error al cancelar la programación:', error);
      const errorMsg = error.response?.data?.error || 'Error al cancelar la programación. Por favor, intenta de nuevo.';
      alert(`❌ ${errorMsg}`);
    } finally {
      setScheduling(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleMetrics = (postId) => {
    navigate(`/metricas/${postId}`);
  };

  const navigateToPost = (postId) => {
    navigate(`/view-post/${postId}`);
  };

  const handleOpenPublicarManual = (post) => {
    setSelectedPostForManual(post);
    setShowPublicarManualModal(true);
  };

  const handleClosePublicarManual = () => {
    setShowPublicarManualModal(false);
    setSelectedPostForManual(null);
  };

  const handlePublicarManual = async (postId, url) => {
    try {
      await contentService.publishManual(postId, url);
      alert('✅ Post publicado manualmente exitosamente!');
      window.location.reload();
    } catch (error) {
      console.error('Error al publicar manualmente:', error);
      throw error; // Re-throw para que el modal lo maneje
    }
  };

  return (
    <div className="posts-table-container">
      <table className="posts-table">
        <thead>
          <tr>
            <th>Plataforma</th>
            <th>Título</th>
            <th>Cuenta</th>
            <th>Desarrolla</th>
            <th>Tipo</th>
            <th>Idea</th>
            <th>Copy</th>
            <th>Contenido</th>
            {filtroEstado === 'proceso' && (
              <th>Avance</th>
            )}
            <th>Fecha</th>
            <th>Hora</th>
            {filtroEstado === 'publicados' && (
              <>
                <th>Likes</th>
                <th>Comentarios</th>
                <th>Shares</th>
                <th>Reposts</th>
                <th>Saved</th>
                <th>Reach</th>
                <th>Interacciones</th>
              </>
            )}
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {posts.map(post => {
            const isInstagramOrTikTok = post.platform_name === 'Instagram' || post.platform_name === 'TikTok';
            const canPublish = isInstagramOrTikTok && post.influencer_instagram_connected;

            // Calcular progreso
            let progress = 0;
            if (post.creative_idea_approved) progress += 33;
            if (post.copy_approved) progress += 33;
            if (post.media_count > 0) progress += 34;

            return (
              <tr key={post.id}>
                <td className="table-platform">
                  <div className="table-platform-content">
                    {post.platform_icon_url && (
                      <img
                        src={post.platform_icon_url}
                        alt={post.platform_name}
                        className="table-platform-icon"
                        title={post.platform_name}
                      />
                    )}
                  </div>
                </td>
                <td className="table-title">{post.title}</td>
                <td className="table-influencer">
                  <div className="table-influencer-content">
                    {post.influencer_profile_image_url ? (
                      <img
                        src={post.influencer_profile_image_url}
                        alt={post.influencer_name}
                        className="table-influencer-avatar"
                        title={post.influencer_name}
                      />
                    ) : (
                      <div className="table-influencer-avatar-placeholder" title={post.influencer_name}>
                        {post.influencer_name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                </td>
                <td className="table-desarrolla">
                  {post.desarrolla_profile_image_url ? (
                    <div className="table-desarrolla-content">
                      <img
                        src={post.desarrolla_profile_image_url}
                        alt={post.desarrolla_name}
                        className="table-desarrolla-avatar"
                        title={post.desarrolla_name}
                      />
                    </div>
                  ) : post.desarrolla_name ? (
                    <div
                      className="table-desarrolla-avatar-placeholder"
                      style={{ backgroundColor: post.desarrolla_color }}
                      title={post.desarrolla_name}
                    >
                      {post.desarrolla_name?.charAt(0) || '?'}
                    </div>
                  ) : null}
                </td>
                <td className="table-material-type">
                  <div className="table-material-badge">
                    {post.material_type === 'video' && <span title="Video">🎥</span>}
                    {post.material_type === 'image' && <span title="Imagen">📸</span>}
                    {post.material_type === 'carousel' && <span title="Carrusel">🖼️</span>}
                    {post.material_type === 'reel' && <span title="Reel">🎬</span>}
                    {post.material_type === 'story' && <span title="Historia">📱</span>}
                    {!post.material_type && <span title="Sin definir">-</span>}
                  </div>
                </td>
                <td className="table-approval">
                  <div className="table-approval-badge">
                    {post.creative_idea_approved && (
                      <span className="approval-icon approved" title="Idea aprobada">✅</span>
                    )}
                  </div>
                </td>
                <td className="table-approval">
                  <div className="table-approval-badge">
                    {post.copy_approved && (
                      <span className="approval-icon approved" title="Copy aprobado">✅</span>
                    )}
                  </div>
                </td>
                <td className="table-media">
                  {post.media_count > 0 && (
                    <div className="table-media-badge">
                      <span>
                        {post.material_type === 'video' ? '🎥' :
                         post.material_type === 'carousel' ? '🖼️' : '📸'}
                      </span>
                      <span>{post.media_count}</span>
                    </div>
                  )}
                </td>
                {filtroEstado === 'proceso' && (
                  <td className="table-progress">
                    <div className="progress-content">
                      {progress}%
                    </div>
                  </td>
                )}
                <td className="table-date">
                  {new Date(post.publication_date).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </td>
                <td className="table-time">
                  {post.publication_time ? post.publication_time.substring(0, 5) : '-'}
                </td>
                {filtroEstado === 'publicados' && (
                  <>
                    <td className="table-metrics">
                      {post.instagram_likes !== null && post.instagram_likes !== undefined ? (
                        <span className="metric-value">
                          <img
                            src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png"
                            alt="likes"
                            className="metric-icon"
                          />
                          {post.instagram_likes.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_comments !== null && post.instagram_comments !== undefined ? (
                        <span className="metric-value">
                          <img
                            src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png"
                            alt="comentarios"
                            className="metric-icon"
                          />
                          {post.instagram_comments.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_shares !== null && post.instagram_shares !== undefined ? (
                        <span className="metric-value">
                          <img
                            src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png"
                            alt="compartidos"
                            className="metric-icon"
                          />
                          {post.instagram_shares.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_reposts !== null && post.instagram_reposts !== undefined ? (
                        <span className="metric-value">
                          <img
                            src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-reposts.png"
                            alt="reposts"
                            className="metric-icon"
                          />
                          {post.instagram_reposts.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_saved !== null && post.instagram_saved !== undefined ? (
                        <span className="metric-value">
                          <img
                            src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png"
                            alt="guardados"
                            className="metric-icon"
                          />
                          {post.instagram_saved.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_reach !== null && post.instagram_reach !== undefined ? (
                        <span className="metric-value">
                          {post.instagram_reach.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                    <td className="table-metrics">
                      {post.instagram_total_interactions !== null && post.instagram_total_interactions !== undefined ? (
                        <span className="metric-value">
                          {post.instagram_total_interactions.toLocaleString()}
                        </span>
                      ) : (
                        <span className="table-no-data">-</span>
                      )}
                    </td>
                  </>
                )}
                <td className="table-actions">
                  <div className="table-actions-buttons">
                    {filtroEstado === 'proceso' ? (
                      <>
                        {(isAprobadorMaestro || progress === 100) && (
                          <button
                            className="table-btn table-btn-approve"
                            onClick={() => handleApprove(post)}
                            disabled={approving[post.id]}
                            title={isAprobadorMaestro && progress < 100 ? `Aprobar post (Avance: ${progress}%)` : 'Aprobar post'}
                          >
                            {approving[post.id] ? '⏳' : 'Aprobar'}
                          </button>
                        )}
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => navigateToPost(post.id)}
                          title="Ver detalles"
                        >
                          →
                        </button>
                      </>
                    ) : filtroEstado === 'aprobados' ? (
                      <>
                        <button
                          className="table-btn table-btn-publish"
                          onClick={() => handlePublishNow(post)}
                          disabled={scheduling[post.id] || publishing[post.id]}
                          title="Publicar ahora"
                        >
                          {publishing[post.id] ? '⏳' : 'Publicar'}
                        </button>
                        <button
                          className="table-btn table-btn-manual"
                          onClick={() => handleOpenPublicarManual(post)}
                          disabled={scheduling[post.id] || publishing[post.id]}
                          title="Publicar manualmente"
                        >
                          📝 Manual
                        </button>
                        <button
                          className="table-btn table-btn-schedule"
                          onClick={() => handleSchedulePost(post)}
                          disabled={scheduling[post.id] || publishing[post.id]}
                          title="Programar post"
                        >
                          {scheduling[post.id] ? '⏳' : 'Programar'}
                        </button>
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => navigateToPost(post.id)}
                          title="Ver detalles"
                        >
                          →
                        </button>
                      </>
                    ) : filtroEstado === 'programados' ? (
                      <>
                        <button
                          className="table-btn table-btn-publish"
                          onClick={() => handlePublishNow(post)}
                          disabled={scheduling[post.id] || publishing[post.id]}
                          title="Publicar ahora"
                        >
                          {publishing[post.id] ? '⏳' : 'Publicar'}
                        </button>
                        <button
                          className="table-btn table-btn-cancel"
                          onClick={() => handleCancel(post)}
                          disabled={scheduling[post.id] || publishing[post.id]}
                          title="Cancelar programación"
                        >
                          {scheduling[post.id] ? '⏳' : 'Cancelar'}
                        </button>
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => navigateToPost(post.id)}
                          title="Ver detalles"
                        >
                          →
                        </button>
                      </>
                    ) : filtroEstado === 'publicados' ? (
                      <>
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => navigateToPost(post.id)}
                          title="Ver detalles"
                        >
                          →
                        </button>
                      </>
                    ) : (
                      <>
                        {canPublish ? (
                          <>
                            <button
                              className="table-btn table-btn-schedule"
                              onClick={() => handleSchedulePost(post)}
                              disabled={scheduling[post.id] || publishing[post.id]}
                              title="Programar post"
                            >
                              {scheduling[post.id] ? '⏳' : '📅'}
                            </button>
                            <button
                              className="table-btn table-btn-publish"
                              onClick={() => handlePublishNow(post)}
                              disabled={scheduling[post.id] || publishing[post.id]}
                              title="Publicar ahora"
                            >
                              {publishing[post.id] ? '⏳' : '🚀'}
                            </button>
                          </>
                        ) : null}
                        <button
                          className="table-btn table-btn-status"
                          onClick={() => handlePublish(post)}
                          disabled={publishingStatus[post.id]}
                          title="Cambiar estado a publicado"
                        >
                          {publishingStatus[post.id] ? '⏳' : '✓'}
                        </button>
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => navigateToPost(post.id)}
                          title="Ver detalles"
                        >
                          →
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Modal de Publicar Manual */}
      {showPublicarManualModal && selectedPostForManual && (
        <PublicarManualModal
          isOpen={showPublicarManualModal}
          onClose={handleClosePublicarManual}
          post={selectedPostForManual}
          onPublish={handlePublicarManual}
        />
      )}
    </div>
  );
};

export default ProgramacionPost;
